import {
  XYPlot,
  XAxis,
  YAxis,
  VerticalBarSeries,
  HorizontalGridLines,
  VerticalGridLines, 
  LineSeries
} from "react-vis";
import { useState , useMemo } from "react";
import { Hint } from "react-vis";
import "react-vis/dist/style.css";
import { FormControlLabel, Switch, Box } from "@material-ui/core"
import useStyles from './style'
import { withStyles } from "@material-ui/core/styles"

export default function NetCapacityChart({ data }) {
  const [showBattery, setShowBattery] = useState(true)
  const [showNetGridExport, setShowNetGridExport] = useState(true)
  const [showGridExport, setShowGridExport] = useState(true)
  const [hoveredPoint, setHoveredPoint] = useState(null)
  const classes = useStyles()

  const ColoredSwitch = withStyles({
    switchBase: {
      color: props => props.switchcolor,
      '&$checked': {
        color: props => props.switchcolor,
      },
      '&$checked + $track': {
        backgroundColor: props => props.switchcolor,
        opacity: 1,
      },
    },
    track: {
      backgroundColor: props => props.switchcolor,
    },
    checked: {
      color: props => props.switchcolor,
    },
  })(Switch);
  
  const Toggle = ({ label, checked, onChange, color }) => {
    return (
      <Box className={classes.switchWrapper}>
        <FormControlLabel
          control={
            <ColoredSwitch
              checked={checked}
              onChange={onChange}
              switchcolor={color}
            />
          }
          label={label}
          labelPlacement="right"
        />
      </Box>
    )
  }

  const yDomainMin = Math.min(
    ...data.map(d =>
      d.value.net_grid_export + d.value.available_battery_capacity_kwh + d.value.grid_export
    ),
    0
  );

  const yDomainMax = Math.max(
    ...data.map(d =>
      d.value.net_grid_export + d.value.available_battery_capacity_kwh + d.value.grid_export
    ),
    0
  );

const valueMap = useMemo(() => {
  const map = {};
  data.forEach((d, index) => {
    map[index] = {
      time: d.time,
      battery: d.value.available_battery_capacity_kwh,
      grid: d.value.net_grid_export,
      gridExport: d.value.grid_export
    };
  });
  return map;
}, [data]);

const stackedData = useMemo(() => {
  return data.map((d,index) => {
    let posBase = 0;
    let negBase = 0;

    const bars = {};

    const addBar = (key, value) => {
      if (value >= 0) {
        bars[key] = {
          x: index,
          y0: posBase,
          y: posBase + value
        };
        posBase += value;
      } else {
        bars[key] = {
          x: index,
          y0: negBase + value,
          y: negBase
        };
        negBase += value;
      }
    };

    if (showNetGridExport) {
      addBar("gridBar", d.value.net_grid_export);
    }

    if (showBattery) {
      addBar("batteryBar", d.value.available_battery_capacity_kwh);
    }

    if (showGridExport) {
      addBar("gridExportBar", d.value.grid_export);
    }

    return bars;
  });
}, [data, showNetGridExport, showBattery, showGridExport]);


const yDomain = useMemo(() => {
  if (!Number.isFinite(yDomainMin) || !Number.isFinite(yDomainMax)) {
    return [-10, 10];
  }

  const min = yDomainMin * 2;
  const max = yDomainMax * 2;

  return [Math.min(min, max), Math.max(min, max)];
}, [yDomainMin, yDomainMax])

const hourlyTicks = useMemo(() => {
  return data
    .map((d, i) => (i % 4 === 0 ? i : null))
    .filter(v => v !== null);
}, [data])

return (
  <Box>
    <Box className={classes.toggleWrapper} >
      <Toggle 
        label="Grid Export"
        checked={showGridExport}
        color="#A1A1A1"
        onChange={() => setShowGridExport(v => !v)}
      />
      <Toggle
        label="Net Grid Export"
        checked={showNetGridExport}
        color="#C8C8C8"
        onChange={() => setShowNetGridExport(v => !v)}
      />
      <Toggle
        label="Available Battery"
        checked={showBattery}
        color="#00C853"
        onChange={() => setShowBattery(v => !v)}
      />
    </Box>
    <XYPlot
      xType="linear"
      width={1200}
      height={400}
      margin={{ left: 70, bottom: 90 }}
      yDomain={yDomain}
    >
      <HorizontalGridLines />

      <XAxis
        title="Time"
        tickValues={hourlyTicks}
        tickFormat={i => data[i]?.time}
        tickLabelAngle={-90}
        style={{ text: { fontSize: '11px' } }}
      />

      <YAxis title="Energy (kWh)" />

      {showNetGridExport && (
        <VerticalBarSeries
          data={stackedData.map(d => d.gridBar).filter(Boolean)}
          color="#C8C8C8"
          cluster="no"
          onValueMouseOver={v =>
            setHoveredPoint({ ...v, type: "grid" })
          }
          onValueMouseOut={() => setHoveredPoint(null)}
        />
      )}

      {showBattery && (
        <VerticalBarSeries
          data={stackedData.map(d => d.batteryBar).filter(Boolean)}
          color="#00C853"
          cluster="no"
          onValueMouseOver={v =>
            setHoveredPoint({ ...v, type: "battery" })
          }
          onValueMouseOut={() => setHoveredPoint(null)}
        />
      )}

      {showGridExport && (
        <VerticalBarSeries
          data={stackedData.map(d => d.gridExportBar).filter(Boolean)}
          color="#A1A1A1"
          cluster="no"
          onValueMouseOver={v =>
            setHoveredPoint({ ...v, type: "gridExport" })
          }
          onValueMouseOut={() => setHoveredPoint(null)}
        />
      )}

      {data[96] && (
        <LineSeries
          data={[
            { x: 96, y: yDomainMin },
            { x: 96, y: yDomainMax }
          ]}
          stroke="red"
          strokeWidth={2}
          strokeDasharray="4 4"
        />
      )}

      {hoveredPoint && (
        <Hint value={hoveredPoint}>
          <Box className={classes.hintWrapper} >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {valueMap[hoveredPoint.x]?.time}
            </div>
            
            {showBattery && (
              <div style={{ color: "#00C853" }}>
                ● Battery:{" "}
                {valueMap[hoveredPoint.x]?.battery !== undefined
                  ? Number(valueMap[hoveredPoint.x].battery).toFixed(2)
                  : "--"}{" "}
                kWh
              </div>
            )}
            {showNetGridExport && (
              <div style={{ color: "#C8C8C8" }}>
                ● Net Grid Export:{" "}
                {valueMap[hoveredPoint.x]?.grid !== undefined
                  ? Number(valueMap[hoveredPoint.x].grid).toFixed(2)
                  : "--"}{" "}
                kWh
              </div>
            )}
             {showGridExport && (
              <div style={{ color: "#A1A1A1" }}>
                ● Grid Export:{" "}
                {valueMap[hoveredPoint.x]?.gridExport !== undefined
                  ? Number(valueMap[hoveredPoint.x].gridExport).toFixed(2)
                  : "--"}{" "}
                kWh
              </div>
            )}
          </Box>
        </Hint>
      )}
    </XYPlot>
  </Box>
  );
}


